import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  User, Mail, Building2, Globe, Lock, Save, Loader2,
  CheckCircle, AlertCircle, ChevronRight, Shield
} from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const SettingsPage = () => {
  const { user, loading: authLoading, checkAuth, adminStatus } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', company: '', role: '', country: '', language: 'en' });
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const [profileMsg, setProfileMsg] = useState(null);
  const [pwMsg, setPwMsg] = useState(null);

  const headers = { 'Content-Type': 'application/json' };

  useEffect(() => {
    if (!authLoading && !user) { navigate('/login', { replace: true }); return; }
    if (user) {
      setForm({
        name: user.name || '',
        company: user.company || '',
        role: user.role || '',
        country: user.country || '',
        language: user.language || 'en',
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- auth check on mount
  }, [user, authLoading]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    setProfileMsg(null);
    try {
      const res = await fetch(`${API}/api/auth/profile`, {
        method: 'PUT', headers, credentials: 'include', body: JSON.stringify(form),
      });
      if (res.ok) {
        setProfileMsg({ type: 'success', text: 'Profile updated successfully.' });
        checkAuth();
      } else {
        setProfileMsg({ type: 'error', text: 'Failed to update profile.' });
      }
    } catch { setProfileMsg({ type: 'error', text: 'Connection error.' }); }
    finally { setSaving(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwMsg(null);
    if (pwForm.newPw.length < 8) { setPwMsg({ type: 'error', text: 'New password must be at least 8 characters.' }); return; }
    if (pwForm.newPw !== pwForm.confirm) { setPwMsg({ type: 'error', text: 'Passwords do not match.' }); return; }
    setSavingPw(true);
    try {
      const res = await fetch(`${API}/api/auth/change-password`, {
        method: 'PUT', headers, credentials: 'include',
        body: JSON.stringify({ current_password: pwForm.current, new_password: pwForm.newPw }),
      });
      if (res.ok) {
        setPwMsg({ type: 'success', text: 'Password changed successfully.' });
        setPwForm({ current: '', newPw: '', confirm: '' });
      } else {
        const data = await res.json();
        setPwMsg({ type: 'error', text: data.detail || 'Failed to change password.' });
      }
    } catch { setPwMsg({ type: 'error', text: 'Connection error.' }); }
    finally { setSavingPw(false); }
  };

  if (authLoading) return <PageLayout><div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-taxera-blue" /></div></PageLayout>;

  const isAdmin = adminStatus?.isAdmin;
  const isOAuth = user?.picture; // Google OAuth users have a picture

  return (
    <PageLayout>
      <div className="max-w-3xl mx-auto px-6 py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="font-heading text-3xl lg:text-4xl font-bold text-taxera-charcoal mb-2" data-testid="settings-title">Account Settings</h1>
          <p className="text-base text-slate-500 mb-8">Manage your profile and security preferences.</p>
        </motion.div>

        {/* Account Overview */}
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="bg-white rounded-2xl border border-slate-200 p-6 mb-6" data-testid="account-overview">
          <div className="flex items-center gap-4">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-14 h-14 rounded-full" />
            ) : (
              <div className="w-14 h-14 rounded-full bg-taxera-blue/10 flex items-center justify-center">
                <User className="w-7 h-7 text-taxera-blue" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h2 className="font-heading text-lg font-bold text-taxera-charcoal">{user?.name}</h2>
              <p className="text-sm text-slate-400">{user?.email}</p>
            </div>
            <div className="flex flex-col items-end gap-1">
              <span className="text-2xs px-2.5 py-0.5 rounded-full font-semibold bg-taxera-teal/10 text-taxera-teal capitalize">{user?.account_type || 'registered'}</span>
              {isAdmin && (
                <span className="text-2xs px-2.5 py-0.5 rounded-full font-semibold bg-amber-50 text-amber-600 flex items-center gap-1">
                  <Shield className="w-2.5 h-2.5" /> Admin
                </span>
              )}
            </div>
          </div>
        </motion.div>

        {/* Profile Form */}
        <motion.form onSubmit={saveProfile} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white rounded-2xl border border-slate-200 p-6 mb-6" data-testid="profile-form">
          <h3 className="font-heading text-base font-bold text-taxera-charcoal mb-4">Profile Information</h3>
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1 block">Full Name</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input type="text" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="w-full border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-taxera-teal" data-testid="settings-name" />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1 block">Company</label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input type="text" value={form.company} onChange={e => setForm(p => ({ ...p, company: e.target.value }))} className="w-full border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-taxera-teal" placeholder="Your company" data-testid="settings-company" />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1 block">Role</label>
              <select value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-taxera-teal" data-testid="settings-role">
                <option value="">Select role...</option>
                <option value="Tax Director">Tax Director</option>
                <option value="IT Director">IT Director</option>
                <option value="CFO">CFO / Finance</option>
                <option value="CIO">CIO / CTO</option>
                <option value="Head of Compliance">Head of Compliance</option>
                <option value="Consultant">Consultant</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1 block">Country</label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input type="text" value={form.country} onChange={e => setForm(p => ({ ...p, country: e.target.value }))} className="w-full border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-taxera-teal" placeholder="e.g. Germany" data-testid="settings-country" />
              </div>
            </div>
          </div>

          {profileMsg && (
            <div className={`flex items-center gap-2 text-sm mb-4 ${profileMsg.type === 'success' ? 'text-emerald-600' : 'text-red-500'}`} data-testid="profile-msg">
              {profileMsg.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
              {profileMsg.text}
            </div>
          )}

          <button type="submit" disabled={saving} className="inline-flex items-center gap-2 px-6 py-2.5 taxera-gradient text-white rounded-full text-sm font-semibold hover:shadow-lg transition-all disabled:opacity-50" data-testid="save-profile-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save Changes
          </button>
        </motion.form>

        {/* Password Change (only for email users) */}
        {!isOAuth && (
          <motion.form onSubmit={changePassword} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="bg-white rounded-2xl border border-slate-200 p-6 mb-6" data-testid="password-form">
            <h3 className="font-heading text-base font-bold text-taxera-charcoal mb-4">Change Password</h3>
            <div className="space-y-4 mb-4">
              <div>
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1 block">Current Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input type="password" value={pwForm.current} onChange={e => setPwForm(p => ({ ...p, current: e.target.value }))} className="w-full border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-taxera-teal" data-testid="current-password" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1 block">New Password</label>
                  <input type="password" value={pwForm.newPw} onChange={e => setPwForm(p => ({ ...p, newPw: e.target.value }))} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-taxera-teal" data-testid="new-password" />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1 block">Confirm Password</label>
                  <input type="password" value={pwForm.confirm} onChange={e => setPwForm(p => ({ ...p, confirm: e.target.value }))} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-taxera-teal" data-testid="confirm-password" />
                </div>
              </div>
            </div>

            {pwMsg && (
              <div className={`flex items-center gap-2 text-sm mb-4 ${pwMsg.type === 'success' ? 'text-emerald-600' : 'text-red-500'}`} data-testid="password-msg">
                {pwMsg.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                {pwMsg.text}
              </div>
            )}

            <button type="submit" disabled={savingPw} className="inline-flex items-center gap-2 px-6 py-2.5 bg-slate-900 text-white rounded-full text-sm font-semibold hover:bg-slate-800 transition-all disabled:opacity-50" data-testid="change-password-btn">
              {savingPw ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Lock className="w-3.5 h-3.5" />}
              Change Password
            </button>
          </motion.form>
        )}

        {/* Quick Links */}
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="quick-links">
          <h3 className="font-heading text-base font-bold text-taxera-charcoal px-6 pt-5 pb-3">Quick Links</h3>
          <div className="divide-y divide-slate-100">
            <a href="/dashboard" className="flex items-center justify-between px-6 py-3.5 hover:bg-slate-50 transition-colors">
              <span className="text-sm text-taxera-charcoal">My Diagnostics</span>
              <ChevronRight className="w-4 h-4 text-slate-400" />
            </a>
            <a href="/evaluate" className="flex items-center justify-between px-6 py-3.5 hover:bg-slate-50 transition-colors">
              <span className="text-sm text-taxera-charcoal">Take Assessment</span>
              <ChevronRight className="w-4 h-4 text-slate-400" />
            </a>
            {isAdmin && (
              <a href="/admin" className="flex items-center justify-between px-6 py-3.5 hover:bg-slate-50 transition-colors" data-testid="admin-link-settings">
                <span className="text-sm text-taxera-charcoal flex items-center gap-2"><Shield className="w-3.5 h-3.5 text-amber-500" /> Admin Portal</span>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </a>
            )}
          </div>
        </motion.div>
      </div>
    </PageLayout>
  );
};

export default SettingsPage;
